import React, { useEffect, useState } from 'react'
import { X, Loader2, BarChart3, Inbox, ChevronDown, ChevronRight, Check, Minus, Sparkles, Lightbulb, Film } from 'lucide-react'
import { resultApi } from '../api/lessonBuilderApi'

interface Props {
  lessonId: string
  lessonTitle?: string
  onClose: () => void
}

// Bảng breakdown TỪNG PHẦN của 1 lượt làm (detail.sections do LessonViewerPage gửi).
const SectionBreakdown: React.FC<{ detail: any }> = ({ detail }) => {
  const sections: any[] = Array.isArray(detail?.sections) ? detail.sections : []

  return (
    <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-3 my-1">
      {/* Tóm tắt tương tác */}
      <div className="flex flex-wrap gap-2 mb-2 text-[11px] font-semibold">
        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Đúng {detail?.correct ?? 0}</span>
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700">✗ Sai {detail?.wrong ?? 0}</span>
        <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 inline-flex items-center gap-1">
          <Lightbulb size={11} /> Gợi ý {detail?.hintsUsed ?? 0}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 inline-flex items-center gap-1">
          <Sparkles size={11} /> Hỏi AI {detail?.aiAsked ?? 0}
        </span>
        {detail?.videoWatched !== undefined && (
          <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 inline-flex items-center gap-1">
            <Film size={11} /> Video {detail.videoWatched}
          </span>
        )}
        {detail?.totalSections !== undefined && (
          <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
            Đã học {detail?.sectionsDone ?? detail?.sectionsViewed ?? 0}/{detail.totalSections} phần · {detail?.contentPercent ?? 0}%
          </span>
        )}
      </div>

      {sections.length === 0 ? (
        <p className="text-[11px] text-gray-400">Lượt làm này chưa có dữ liệu chi tiết theo từng phần (bản ghi cũ).</p>
      ) : (
        <table className="w-full text-xs bg-white rounded-lg overflow-hidden">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-100 bg-gray-50/60">
              <th className="py-1.5 px-2">Phần</th>
              <th className="py-1.5 px-2 text-center">Đã xem</th>
              <th className="py-1.5 px-2 text-center">Câu hỏi</th>
              <th className="py-1.5 px-2 text-center text-green-600">Đúng</th>
              <th className="py-1.5 px-2 text-center text-red-500">Sai</th>
              <th className="py-1.5 px-2 text-center text-yellow-600">Gợi ý</th>
              <th className="py-1.5 px-2 text-center text-purple-600">Hỏi AI</th>
              <th className="py-1.5 px-2 text-center">Đã học</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sections.map((s) => {
              const answered = (s.correct || 0) + (s.wrong || 0)
              const pending = (s.totalQuestions || 0) - answered
              return (
                <tr key={s.idx} className={s.done ? '' : 'bg-amber-50/40'}>
                  <td className="py-1.5 px-2 font-medium text-gray-700">{s.idx + 1}. {s.title}</td>
                  <td className="py-1.5 px-2 text-center">
                    {s.viewed ? <Check size={13} className="text-green-600 inline" /> : <Minus size={13} className="text-gray-300 inline" />}
                  </td>
                  <td className="py-1.5 px-2 text-center text-gray-500">{s.totalQuestions}</td>
                  <td className="py-1.5 px-2 text-center font-semibold text-green-700">{s.correct}</td>
                  <td className="py-1.5 px-2 text-center font-semibold text-red-600">{s.wrong}</td>
                  <td className="py-1.5 px-2 text-center text-yellow-700">{s.hints}</td>
                  <td className="py-1.5 px-2 text-center text-purple-700">{s.ai}</td>
                  <td className="py-1.5 px-2 text-center">
                    {s.done
                      ? <Check size={13} className="text-green-600 inline" />
                      : <span className="text-[10px] text-amber-600 font-semibold">
                          {!s.viewed ? 'chưa xem' : pending > 0 ? `còn ${pending} câu` : 'chưa xong'}
                        </span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

const ResultsModal: React.FC<Props> = ({ lessonId, lessonTitle, onClose }) => {
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    resultApi.list(lessonId).then((r) => {
      setRows(r)
      setLoading(false)
    })
  }, [lessonId])

  const avg =
    rows.length > 0
      ? rows.reduce((s, r) => s + (r.max_score ? (r.score / r.max_score) * 10 : 0), 0) / rows.length
      : 0

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <BarChart3 className="text-blue-600" size={20} />
            <h2 className="text-lg font-black text-gray-900">Kết quả học tập</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={22} /></button>
        </div>

        <div className="px-4 py-2 border-b border-gray-50 text-sm text-gray-500">
          {lessonTitle && <span className="font-semibold text-gray-700">{lessonTitle}</span>}
          {rows.length > 0 && <span> · {rows.length} lượt làm · TB quy đổi 10: <strong className="text-blue-700">{avg.toFixed(1)}</strong></span>}
          {rows.length > 0 && <span className="text-[11px] text-gray-400"> — bấm vào học sinh để xem chi tiết từng phần</span>}
        </div>

        <div className="overflow-y-auto p-3">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-gray-300">
              <Inbox size={40} className="mb-2" />
              <p className="text-gray-400 text-sm">Chưa có lượt làm bài nào.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100">
                  <th className="py-2 px-2">Học sinh</th>
                  <th className="py-2 px-2">Điểm</th>
                  <th className="py-2 px-2">Đúng</th>
                  <th className="py-2 px-2">Thời gian</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((r) => {
                  const hasDetail = r.detail && typeof r.detail === 'object' && !Array.isArray(r.detail)
                  const open = expanded === r.id
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        onClick={() => hasDetail && setExpanded(open ? null : r.id)}
                        className={hasDetail ? 'cursor-pointer hover:bg-blue-50/40' : ''}
                      >
                        <td className="py-2 px-2">
                          <div className="font-semibold text-gray-800 flex items-center gap-1">
                            {hasDetail && (open ? <ChevronDown size={14} className="text-blue-500" /> : <ChevronRight size={14} className="text-gray-300" />)}
                            {r.student_name || '—'}
                          </div>
                          <div className="text-[11px] text-gray-400">{r.student_email}</div>
                        </td>
                        <td className="py-2 px-2 font-bold text-blue-700">{r.score}/{r.max_score}</td>
                        <td className="py-2 px-2 text-gray-600">{r.correct}/{r.total}</td>
                        <td className="py-2 px-2 text-gray-400 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString('vi-VN') : ''}</td>
                      </tr>
                      {open && hasDetail && (
                        <tr>
                          <td colSpan={4} className="px-2 pb-2">
                            <SectionBreakdown detail={r.detail} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResultsModal
